/**
 * Enums do Meta Ads — espelho exato de `prisma/schema.prisma` §13.
 *
 * Mesma regra do resto de `packages/shared` (RNF-121): divergência entre este
 * arquivo e o schema é erro de build, não erro de execução. O teste de unidade
 * `meta-ads-enums.spec.ts` compara as duas listas.
 *
 * Módulo puro, sem dependência.
 */

// ---------------------------------------------------------------------------
// Estrutura
// ---------------------------------------------------------------------------

export const OPERACOES_ANUNCIO = ['VENDA', 'ALUGUEL', 'OUTRA'] as const
export type OperacaoAnuncio = (typeof OPERACOES_ANUNCIO)[number]

export const NIVEIS_ANUNCIO = ['CONTA', 'CAMPANHA', 'CONJUNTO', 'ANUNCIO'] as const
export type NivelAnuncio = (typeof NIVEIS_ANUNCIO)[number]

export const STATUS_ENTIDADE_META = [
  'ATIVO',
  'PAUSADO',
  'ARQUIVADO',
  'EXCLUIDO',
  'EM_ANALISE',
  'REPROVADO',
  'DESCONHECIDO',
] as const
export type StatusEntidadeMeta = (typeof STATUS_ENTIDADE_META)[number]

export const NIVEIS_ORCAMENTO = ['CAMPANHA', 'CONJUNTO', 'INDEFINIDO'] as const
export type NivelOrcamento = (typeof NIVEIS_ORCAMENTO)[number]

export const TIPOS_MIDIA_CRIATIVO = [
  'IMAGEM',
  'VIDEO',
  'CARROSSEL',
  'OUTRO',
  'DESCONHECIDO',
] as const
export type TipoMidiaCriativo = (typeof TIPOS_MIDIA_CRIATIVO)[number]

// ---------------------------------------------------------------------------
// Análise e otimização
// ---------------------------------------------------------------------------

export const TIPOS_RECOMENDACAO_META = [
  'AUMENTAR_ORCAMENTO',
  'REDUZIR_ORCAMENTO',
  'PAUSAR',
  'REATIVAR',
  'TROCAR_CRIATIVO',
  'REDISTRIBUIR_ORCAMENTO',
  'INVESTIGAR',
  'MANTER',
] as const
export type TipoRecomendacaoMeta = (typeof TIPOS_RECOMENDACAO_META)[number]

export const SEVERIDADES_RECOMENDACAO = [
  'OPORTUNIDADE',
  'INFORMATIVO',
  'ATENCAO',
  'CRITICO',
] as const
export type SeveridadeRecomendacao = (typeof SEVERIDADES_RECOMENDACAO)[number]

export const STATUS_RECOMENDACAO_META = ['ABERTA', 'APLICADA', 'DESCARTADA', 'EXPIRADA'] as const
export type StatusRecomendacaoMeta = (typeof STATUS_RECOMENDACAO_META)[number]

export const ACOES_OTIMIZACAO_META = [
  'PAUSAR_ANUNCIO',
  'ATIVAR_ANUNCIO',
  'PAUSAR_CONJUNTO',
  'ATIVAR_CONJUNTO',
  'PAUSAR_CAMPANHA',
  'ATIVAR_CAMPANHA',
  'ALTERAR_ORCAMENTO',
  'CRIAR_VARIACAO',
  'DUPLICAR_ANUNCIO',
] as const
export type AcaoOtimizacaoMeta = (typeof ACOES_OTIMIZACAO_META)[number]

export const STATUS_ACAO_OTIMIZACAO = ['PENDENTE', 'EXECUTADA', 'FALHOU', 'REVERTIDA'] as const
export type StatusAcaoOtimizacao = (typeof STATUS_ACAO_OTIMIZACAO)[number]

export const STATUS_VARIACAO_CRIATIVO = ['RASCUNHO', 'APROVADA', 'REJEITADA', 'PUBLICADA'] as const
export type StatusVariacaoCriativo = (typeof STATUS_VARIACAO_CRIATIVO)[number]

// ---------------------------------------------------------------------------
// Classificações derivadas (não persistidas)
// ---------------------------------------------------------------------------

/**
 * Faixa do score. Verde/amarelo/vermelho da TELA-META-01.
 *
 * `SEM_DADOS` é a quarta faixa e não um caso de erro: um anúncio recém-publicado
 * com 40 impressões **não tem** performance ruim — não tem performance nenhuma. As
 * três faixas de cor sem esta quarta forçariam a escolha entre chamá-lo de crítico
 * (falso alarme na primeira semana de toda campanha nova) ou de excelente (pior).
 */
export const FAIXAS_SCORE = ['EXCELENTE', 'ATENCAO', 'CRITICO', 'SEM_DADOS'] as const
export type FaixaScore = (typeof FAIXAS_SCORE)[number]

/**
 * Grau de suspeita de fadiga de criativo.
 *
 * Três graus e nenhum deles é afirmação, por exigência explícita do §9 da missão:
 * "não afirmar que existe fadiga como verdade absoluta". Frequência alta com CTR em
 * queda é *sinal*; a causa pode ser sazonalidade, concorrência no leilão ou
 * mudança de público.
 */
export const GRAUS_FADIGA = ['SEM_SINAL', 'POSSIVEL', 'PROVAVEL'] as const
export type GrauFadiga = (typeof GRAUS_FADIGA)[number]

/** Direção de uma variação entre períodos. */
export const TENDENCIAS = ['SUBINDO', 'ESTAVEL', 'CAINDO', 'INDETERMINADA'] as const
export type Tendencia = (typeof TENDENCIAS)[number]

/**
 * Tipos de anomalia que o diagnóstico sabe detectar (§8 da missão).
 *
 * Lista fechada e não texto livre: cada tipo tem limiar próprio, explicação própria
 * e um teste que fixa quando ele dispara e quando não dispara.
 */
export const TIPOS_ANOMALIA = [
  'CPL_SUBINDO',
  'CTR_CAINDO',
  'CPC_SUBINDO',
  'CPM_ANORMAL',
  'FREQUENCIA_ELEVADA',
  'GASTO_SEM_LEAD',
  'SEM_ENTREGA',
  'QUEDA_REPENTINA',
  'CUSTO_MUITO_ACIMA_DA_MEDIA',
  'DESEMPENHO_MUITO_ACIMA_DA_MEDIA',
  'TENDENCIA_POSITIVA',
  'CONCENTRACAO_ORCAMENTO',
] as const
export type TipoAnomalia = (typeof TIPOS_ANOMALIA)[number]

// ---------------------------------------------------------------------------
// Ajudantes
// ---------------------------------------------------------------------------

const CONJUNTO_NIVEIS: ReadonlySet<string> = new Set(NIVEIS_ANUNCIO)

export function ehNivelAnuncio(valor: string): valor is NivelAnuncio {
  return CONJUNTO_NIVEIS.has(valor)
}

/**
 * Um status que ainda gasta dinheiro.
 *
 * Usado pelo diagnóstico para não gerar recomendação sobre o que já está parado:
 * "pausar este anúncio" num anúncio pausado é ruído que faz o gestor desconfiar da
 * lista inteira.
 */
export function estaVeiculando(status: StatusEntidadeMeta): boolean {
  return status === 'ATIVO'
}

/** Status terminal na Meta — não volta a veicular sem intervenção estrutural. */
export function ehStatusEncerrado(status: StatusEntidadeMeta): boolean {
  return status === 'ARQUIVADO' || status === 'EXCLUIDO'
}

/**
 * Tradução do `status`/`effective_status` da Meta para o enum normalizado.
 *
 * Aceita qualquer string e **nunca lança**: a lista de `effective_status` da Meta
 * cresce sem aviso (`PENDING_BILLING_INFO`, `PREAPPROVED`, `ADSET_PAUSED`…), e um
 * valor desconhecido derrubando a sincronização faria o painel inteiro parar por
 * causa de um estado novo que a Meta inventou. O valor cru é guardado ao lado, em
 * `status_externo`.
 */
export function normalizarStatusMeta(bruto: string | null | undefined): StatusEntidadeMeta {
  if (typeof bruto !== 'string') return 'DESCONHECIDO'

  switch (bruto.trim().toUpperCase()) {
    case 'ACTIVE':
      return 'ATIVO'
    case 'PAUSED':
    // Pausa herdada do nível acima: para o gestor, o anúncio está parado, e é isso
    // que a coluna de status precisa dizer. Qual nível pausou aparece no detalhe.
    case 'CAMPAIGN_PAUSED':
    case 'ADSET_PAUSED':
      return 'PAUSADO'
    case 'ARCHIVED':
      return 'ARQUIVADO'
    case 'DELETED':
      return 'EXCLUIDO'
    case 'PENDING_REVIEW':
    case 'IN_PROCESS':
    case 'PENDING_BILLING_INFO':
    case 'PREAPPROVED':
      return 'EM_ANALISE'
    case 'DISAPPROVED':
    case 'WITH_ISSUES':
      return 'REPROVADO'
    default:
      return 'DESCONHECIDO'
  }
}
